/**
 * Token API - Token balance and transaction endpoints
 * Extracted from backendService.ts to improve modularity
 */

import { logInfo, logAPIError } from '../../utils/errorLogger'
import type {
  TokenBalance,
  TokenHistoryResponse,
  AddTokensRequest,
  AddTokensResponse,
  DoctorAiTokenBalance
} from '../types'

/**
 * Get token balance for a specific doctor (admin only)
 * @param doctorId - Doctor ID
 */
export async function getDoctorTokenBalance(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  doctorId: string
): Promise<TokenBalance> {
  try {
    logInfo(`Fetching token balance for doctor: ${doctorId}`, 'Backend')

    const response = await makeRequest<TokenBalance>(`/api/admin/doctors/${doctorId}/tokens`, {
      method: 'GET'
    })

    logInfo(`Doctor ${doctorId} balance: ${response.balance} tokens`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch token balance'
    logAPIError(`/api/admin/doctors/${doctorId}/tokens`, 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Add tokens to a doctor's account (admin only)
 * @param doctorId - Doctor ID
 * @param request - Token addition request
 */
export async function addDoctorTokens(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  doctorId: string,
  request: AddTokensRequest
): Promise<AddTokensResponse> {
  try {
    logInfo(`Adding ${request.amount} tokens to doctor ${doctorId}: ${request.description}`, 'Backend')

    const response = await makeRequest<AddTokensResponse>(`/api/admin/doctors/${doctorId}/tokens/add`, {
      method: 'POST',
      body: JSON.stringify(request)
    })

    logInfo(`Tokens added successfully. New balance: ${response.newBalance}`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to add tokens'
    logAPIError(`/api/admin/doctors/${doctorId}/tokens/add`, 'POST', 0, errorMessage, request)
    throw error
  }
}

/**
 * Get token transaction history for a doctor (admin only)
 * @param doctorId - Doctor ID
 * @param page - Page number (1-indexed)
 * @param limit - Number of transactions per page
 */
export async function getDoctorTokenHistory(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  doctorId: string,
  page: number = 1,
  limit: number = 20
): Promise<TokenHistoryResponse> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    })

    const url = `/api/admin/doctors/${doctorId}/tokens/history?${params.toString()}`
    logInfo(`Fetching token history for doctor ${doctorId}: page=${page}, limit=${limit}`, 'Backend')

    const response = await makeRequest<TokenHistoryResponse>(url, {
      method: 'GET'
    })

    logInfo(`Fetched ${response.transactions.length} transactions (total: ${response.totalCount})`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch token history'
    logAPIError(`/api/admin/doctors/${doctorId}/tokens/history`, 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Get token balance for the current authenticated doctor
 */
export async function getTokenBalance(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>
): Promise<TokenBalance> {
  try {
    logInfo('Fetching token balance for current doctor', 'Backend')

    const response = await makeRequest<TokenBalance>('/api/doctors/me/tokens', {
      method: 'GET',
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
      cache: 'no-cache'
    })

    logInfo(`Current doctor balance: ${response.balance} tokens`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch token balance'
    logAPIError('/api/doctors/me/tokens', 'GET', 0, errorMessage)
    throw error
  }
}

// ── Doctor AI Token endpoints (separate wallet from regular doctor tokens) ──

/**
 * Get AI token balance for a specific doctor (admin only)
 */
export async function getDoctorAiTokenBalance(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  doctorId: string
): Promise<DoctorAiTokenBalance> {
  try {
    logInfo(`Fetching AI token balance for doctor: ${doctorId}`, 'Backend')

    const response = await makeRequest<DoctorAiTokenBalance>(`/api/admin/doctors/${doctorId}/ai-tokens`, {
      method: 'GET'
    })

    logInfo(`Doctor ${doctorId} AI token balance: ${response.balance}`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch AI token balance'
    logAPIError(`/api/admin/doctors/${doctorId}/ai-tokens`, 'GET', 0, errorMessage)
    throw error
  }
}

/**
 * Credit (add) AI tokens to a doctor's account (admin only)
 */
export async function creditDoctorAiTokens(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  doctorId: string,
  amount: number,
  reason: string
): Promise<DoctorAiTokenBalance> {
  try {
    logInfo(`Adding ${amount} AI tokens to doctor ${doctorId}: ${reason}`, 'Backend')

    const response = await makeRequest<DoctorAiTokenBalance>(`/api/admin/doctors/${doctorId}/ai-tokens/credit`, {
      method: 'POST',
      body: JSON.stringify({ amount, reason })
    })

    logInfo(`AI tokens credited. New balance: ${response.balance}`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to credit AI tokens'
    logAPIError(`/api/admin/doctors/${doctorId}/ai-tokens/credit`, 'POST', 0, errorMessage, { amount, reason })
    throw error
  }
}

/**
 * Debit (deduct) AI tokens from a doctor's account (admin only)
 */
export async function debitDoctorAiTokens(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>,
  doctorId: string,
  amount: number,
  reason: string
): Promise<DoctorAiTokenBalance> {
  try {
    logInfo(`Debiting ${amount} AI tokens from doctor ${doctorId}: ${reason}`, 'Backend')

    const response = await makeRequest<DoctorAiTokenBalance>(`/api/admin/doctors/${doctorId}/ai-tokens/debit`, {
      method: 'POST',
      body: JSON.stringify({ amount, reason })
    })

    logInfo(`AI tokens debited. New balance: ${response.balance}`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to debit AI tokens'
    logAPIError(`/api/admin/doctors/${doctorId}/ai-tokens/debit`, 'POST', 0, errorMessage, { amount, reason })
    throw error
  }
}

/**
 * Get AI token balance for the current authenticated doctor (self-service)
 */
export async function getMyAiTokenBalance(
  makeRequest: <T>(endpoint: string, options?: RequestInit) => Promise<T>
): Promise<DoctorAiTokenBalance> {
  try {
    logInfo('Fetching AI token balance for current doctor', 'Backend')

    const response = await makeRequest<DoctorAiTokenBalance>('/api/doctors/me/ai-tokens', {
      method: 'GET',
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
      cache: 'no-cache'
    })

    logInfo(`Current doctor AI token balance: ${response.balance}`, 'Backend')
    return response
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch AI token balance'
    logAPIError('/api/doctors/me/ai-tokens', 'GET', 0, errorMessage)
    throw error
  }
}
